import React, {useEffect} from "react";
import {Dispatch, SetStateAction} from "react";
import {Input} from "../../ui/input";
import {cn} from "../../ui/lib/utils";
import {v4 as uuid} from 'uuid';
import {PlusIcon, XIcon, TrendingUpDown} from "lucide-react";
import {Tooltip, TooltipContent, TooltipTrigger} from "../../ui/tooltip";

// Enum for transform types
export enum TransformTypes {
    Attribute = "Attribute",
    // Add more transform types in the future
}

// Define the Transform interface based on the backend definition
export interface Transform {
    type: TransformTypes
    transformAttribute?: TransformAttribute
}

export interface TransformAttribute {
    id: string; // Added for frontend tracking
    outputAttributeKey: string;
    transformationFunction: string;
}

interface TransformSelectorProps {
    transform: Transform;
    setTransforms: Dispatch<SetStateAction<Transform[]>>
}

export function TransformSelector(props: TransformSelectorProps) {
    // Get the current payload values or initialize with empty strings
    const [transformFunction, setTransformFunction] = React.useState<string>(
        props.transform.transformAttribute ? props.transform.transformAttribute?.transformationFunction : ""
    );
    const [outputKey, setOutputKey] = React.useState<string>(
        props.transform.transformAttribute ? props.transform.transformAttribute?.outputAttributeKey : ""
    );

    // Function to update the transform
    function updateTransform() {
        props.setTransforms((prev: Transform[]) => {
            const updatedTransforms: Transform[] = prev.map((t: Transform) => {
                if (t.transformAttribute?.id === props.transform.transformAttribute?.id) {
                    return {
                        ...t,
                        transformAttribute: {
                            id: props.transform.transformAttribute?.id || "",
                            outputAttributeKey: outputKey,
                            transformationFunction: transformFunction
                        },
                    };
                }
                return t;
            });

            return updatedTransforms;
        });
    }

    // Function to get transform type display name
    function getTransformTypeName(): string {
        // For now, just return "Attribute Transform" since we only have one type
        return "Attribute Transform";
    }

    return (
        <div className={"flex flex-col w-max"}>
            <div className={"bg-border h-4 w-[1px] ml-2"}></div>
            <div className={"group flex"}>
                <div
                    className={"flex border min-h-[48px] rounded grow group-hover:border-y group-hover:rounded-r-none group-hover:border-l"}>
                    <div
                        className={"bg-backgrounddark flex flex-col justify-center items-center text-center text-textmedium p-2 border-r"}>
                        <TrendingUpDown className={"text-textmedium w-6 h-4"}/>
                    </div>
                    <div
                        className="flex w-full flex-col items-start justify-between px-4 py-3 sm:flex-row sm:items-center bg-backgroundmedium">
                        <p className="text-sm font-medium leading-none">
                                    <span
                                        className="rounded-lg bg-secondarytransparenter px-2 py-1 text-xs text-textmedium border border-secondary">
                                        {getTransformTypeName()}
                                    </span>
                        </p>
                    </div>
                    <div className="text-center text-textmedium flex min-h-[48px] rounded grow border-l rounded-l-none">
                        <div className="flex items-center w-full p-2 gap-1">
                            <Input
                                value={transformFunction.toString()}
                                placeholder="Enter transformation function"
                                onBlur={() => updateTransform()}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        updateTransform();
                                    }
                                }}
                                onChange={(e) => setTransformFunction(e.target.value)}
                                className={cn("h-full w-[320px] text-textlight shadow-none border-none")}
                            />
                            <span className="text-sm font-medium mx-1">→</span>
                            <Input
                                value={outputKey}
                                placeholder="Enter output attribute key"
                                onBlur={() => updateTransform()}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        updateTransform();
                                    }
                                }}
                                onChange={(e) => setOutputKey(e.target.value)}
                                className={cn("h-full w-[220px] text-textlight shadow-none border-none")}
                            />
                        </div>
                    </div>
                </div>
                <div className={"bg-background group-hover:flex group-hover:items-center"}>
                    <div
                        onClick={() => {
                            props.setTransforms((prevTransforms: any) => {
                                let currTransformIndex = prevTransforms.findIndex((value: any) => value !== undefined && value.transformAttribute?.id === props.transform.transformAttribute?.id);
                                if (currTransformIndex === -1) {
                                    return prevTransforms;
                                }
                                if (prevTransforms[currTransformIndex] !== undefined) {
                                    delete prevTransforms[currTransformIndex];
                                }
                                prevTransforms = prevTransforms.filter((value: any) => value !== undefined);
                                return [...prevTransforms];
                            })
                        }}
                        className="h-full pt-[10px] hover:cursor-pointer hover:bg-primary hidden group-hover:block group-hover:border-t group-hover:border-b group-hover:border-r group-hover:rounded-r text-textmedium">
                        <XIcon/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function AddTransformButton(props: {
    setTransforms: Dispatch<SetStateAction<Transform[]>>,
    setIsTransformsVisible?: Dispatch<SetStateAction<boolean>>
    isMinimalStyle: boolean
}) {
    const onClickHandler = () => {
        props.setTransforms(
            (prev: Transform[]) => {
                if (prev === undefined) {
                    return [{
                        type: TransformTypes.Attribute,
                        transformAttribute: {
                            id: uuid(),
                            outputAttributeKey: "outputAttribute",
                            transformationFunction: "extract(inputAttribute, regex)",
                        }
                    }]
                }
                return [
                    ...prev,
                    // Add a new transform with a unique ID and default values
                    {
                        type: TransformTypes.Attribute,
                        transformAttribute: {
                            id: uuid(),
                            outputAttributeKey: "outputAttribute",
                            transformationFunction: "extract(inputAttribute, regex)",
                        }
                    }
                ]
            });
        // Set transforms to visible when adding a new transform
        if (props.setIsTransformsVisible) {
            props.setIsTransformsVisible(true);
        }
    }


    if (props.isMinimalStyle) {
        return <div
            className={"px-2 border-r border-border h-full flex w-max gap-x-1 text-textmedium hover:cursor-pointer hover:text-textlight hover:bg-backgroundlight"}
            onClick={onClickHandler}>
            <Tooltip delayDuration={20}>
                <TooltipTrigger><TrendingUpDown className={"w-4 h-4"}></TrendingUpDown></TooltipTrigger>
                <TooltipContent>Add Transform</TooltipContent>

            </Tooltip>

        </div>
    }

    return <div
        className={"flex w-max bg-background border rounded gap-x-1 p-1 pr-2 text-textmedium hover:cursor-pointer hover:text-textlight"}
        onClick={onClickHandler}>

        <div className={"flex items-center justify-center"}>
            <PlusIcon className={"w-4 h-4"}></PlusIcon>
        </div>
        <div>Add transform</div>
    </div>
}
